import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CATEGORIES: Record<string, string> = {
  salary: 'Цалин',
  utilities: 'Коммунал',
  maintenance: 'Засвар',
  cleaning: 'Цэвэрлэгээ',
  security: 'Аюулгүй байдал',
  insurance: 'Даатгал',
  elevator: 'Лифт',
  landscaping: 'Тохижилт',
  legal: 'Хууль зүй',
  office: 'Оффис',
  reserve_fund: 'Нөөц сан',
  other: 'Бусад',
};

const CAT_COLORS = ['#236844','#2f7d52','#3c9263','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#ec4899','#14b8a6','#f97316','#6366f1','#94a3b8'];

interface BudgetItem {
  id?: string;
  category: string;
  description: string;
  planned_amount: number;
  actual_amount?: number;
}

interface Budget {
  id: string;
  name: string;
  year: number;
  month: number | null;
  status: string;
  total_planned: number;
  total_actual: number;
  notes: string | null;
  items: BudgetItem[];
}

interface Expense {
  id: string;
  budget_id: string | null;
  category: string;
  description: string;
  amount: number;
  expense_date: string;
  vendor: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Ноорог',
  approved: 'Батлагдсан',
  active: 'Идэвхтэй',
  closed: 'Хаагдсан',
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#94a3b8',
  approved: '#3b82f6',
  active: '#236844',
  closed: '#6b7280',
};

export default function BudgetPage() {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'budgets' | 'expenses'>('budgets');

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editBudget, setEditBudget] = useState<Budget | null>(null);
  const [budgetForm, setBudgetForm] = useState({ name: '', year: 2026, month: '' as string, notes: '', items: [] as BudgetItem[] });

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ budget_id: '', category: 'other', description: '', amount: '', expense_date: '', vendor: '' });

  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [b, e] = await Promise.all([
        api.get('/admin/budgets?limit=100'),
        api.get('/admin/expenses?limit=100'),
      ]);
      setBudgets(b?.items || []);
      setExpenses(e?.items || []);
    } catch {}
    setLoading(false);
  };

  const loadSummary = async (budgetId: string) => {
    try {
      const s = await api.get(`/admin/budgets/${budgetId}/summary`);
      setSummary(s);
      setSelectedBudget(budgetId);
    } catch {}
  };

  const handleSaveBudget = async () => {
    const payload = {
      name: budgetForm.name,
      year: budgetForm.year,
      month: budgetForm.month ? parseInt(budgetForm.month) : null,
      notes: budgetForm.notes || null,
      items: budgetForm.items,
    };
    try {
      if (editBudget) {
        await api.put(`/admin/budgets/${editBudget.id}`, payload);
      } else {
        await api.post('/admin/budgets', payload);
      }
      setShowBudgetModal(false);
      setEditBudget(null);
      loadData();
    } catch (e: any) {
      alert(e?.message || 'Алдаа гарлаа');
    }
  };

  const handleApproveBudget = async (id: string) => {
    try {
      await api.post(`/admin/budgets/${id}/approve`, {});
      loadData();
    } catch (e: any) {
      alert(e?.message || 'Алдаа');
    }
  };

  const handleDeleteBudget = async (id: string) => {
    if (!window.confirm('Төсвийг устгах уу?')) return;
    try {
      await api.delete(`/admin/budgets/${id}`);
      loadData();
    } catch (e: any) {
      alert(e?.message || 'Алдаа');
    }
  };

  const handleSaveExpense = async () => {
    try {
      await api.post('/admin/expenses', {
        budget_id: expenseForm.budget_id || null,
        category: expenseForm.category,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        expense_date: expenseForm.expense_date,
        vendor: expenseForm.vendor || null,
      });
      setShowExpenseModal(false);
      loadData();
      if (selectedBudget) loadSummary(selectedBudget);
    } catch (e: any) {
      alert(e?.message || 'Алдаа');
    }
  };

  const addBudgetItem = () => {
    setBudgetForm(f => ({ ...f, items: [...f.items, { category: 'other', description: '', planned_amount: 0 }] }));
  };

  const removeBudgetItem = (idx: number) => {
    setBudgetForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const openNewBudget = () => {
    setEditBudget(null);
    setBudgetForm({ name: '', year: 2026, month: '', notes: '', items: [] });
    setShowBudgetModal(true);
  };

  const openEditBudget = (b: Budget) => {
    setEditBudget(b);
    setBudgetForm({
      name: b.name,
      year: b.year,
      month: b.month ? String(b.month) : '',
      notes: b.notes || '',
      items: b.items.map(i => ({ category: i.category, description: i.description, planned_amount: i.planned_amount })),
    });
    setShowBudgetModal(true);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#5f6f66' }}>Уншиж байна...</div>;

  const totalPlanned = budgets.reduce((s, b) => s + b.total_planned, 0);
  const totalActual = budgets.reduce((s, b) => s + b.total_actual, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, fontSize: 24, color: '#1c2b22', margin: 0 }}>Төсөв удирдлага</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={openNewBudget} style={btnStyle('#236844')}>+ Төсөв үүсгэх</button>
          <button onClick={() => { setExpenseForm({ budget_id: '', category: 'other', description: '', amount: '', expense_date: new Date().toISOString().slice(0, 10), vendor: '' }); setShowExpenseModal(true); }} style={btnStyle('#8b5cf6')}>+ Зардал бүртгэх</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Нийт төсөв" value={budgets.length} color="#236844" />
        <StatCard label="Төлөвлөсөн" value={`₮${totalPlanned.toLocaleString()}`} color="#3b82f6" />
        <StatCard label="Зарцуулсан" value={`₮${totalActual.toLocaleString()}`} color="#f59e0b" />
        <StatCard label="Гүйцэтгэл" value={totalPlanned > 0 ? `${(totalActual / totalPlanned * 100).toFixed(1)}%` : '0%'} color="#8b5cf6" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
        {(['budgets', 'expenses'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 24px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            background: tab === t ? '#236844' : '#e8e5dc', color: tab === t ? '#fff' : '#5f6f66',
            borderRadius: t === 'budgets' ? '8px 0 0 8px' : '0 8px 8px 0',
          }}>
            {t === 'budgets' ? 'Төсвүүд' : 'Зардлууд'}
          </button>
        ))}
      </div>

      {tab === 'budgets' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedBudget ? '1fr 1fr' : '1fr', gap: 20 }}>
          <div>
            {budgets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Төсөв олдсонгүй</div>
            ) : budgets.map(b => (
              <div key={b.id} onClick={() => loadSummary(b.id)} style={{
                background: selectedBudget === b.id ? '#f0fdf4' : '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer',
                border: selectedBudget === b.id ? '2px solid #236844' : '2px solid transparent',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: '#1c2b22' }}>{b.name}</span>
                  <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12, background: STATUS_COLORS[b.status] + '20', color: STATUS_COLORS[b.status], fontWeight: 600 }}>
                    {STATUS_LABELS[b.status] || b.status}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#5f6f66', marginBottom: 8 }}>
                  <span>{b.year} он{b.month ? `, ${b.month}-р сар` : ''}</span>
                  <span>Төлөвлөсөн: ₮{b.total_planned.toLocaleString()}</span>
                  <span>Зарцуулсан: ₮{b.total_actual.toLocaleString()}</span>
                </div>
                <div style={{ height: 6, background: '#e8e5dc', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, b.total_planned > 0 ? b.total_actual / b.total_planned * 100 : 0)}%`, background: b.total_actual > b.total_planned ? '#ef4444' : '#236844', borderRadius: 3, transition: 'width .3s' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  {b.status === 'draft' && (
                    <>
                      <button onClick={e => { e.stopPropagation(); openEditBudget(b); }} style={smallBtn('#3b82f6')}>Засах</button>
                      <button onClick={e => { e.stopPropagation(); handleApproveBudget(b.id); }} style={smallBtn('#236844')}>Батлах</button>
                      <button onClick={e => { e.stopPropagation(); handleDeleteBudget(b.id); }} style={smallBtn('#ef4444')}>Устгах</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selectedBudget && summary && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginTop: 0, marginBottom: 4 }}>{summary.budget_name}</h3>
              <div style={{ fontSize: 13, color: '#5f6f66', marginBottom: 16 }}>
                Гүйцэтгэл: {summary.execution_rate}% | Зөрүү: ₮{summary.variance.toLocaleString()}
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={summary.items.map((it: any) => ({ ...it, name: CATEGORIES[it.category] || it.category }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e5dc" />
                  <XAxis dataKey="name" tick={{ fill: '#5f6f66', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5f6f66', fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => `₮${Number(v).toLocaleString()}`} />
                  <Bar dataKey="planned" name="Төлөвлөсөн" fill="#e8e5dc" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" name="Зарцуулсан" radius={[4, 4, 0, 0]}>
                    {summary.items.map((_: any, i: number) => (
                      <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e8e5dc' }}>
                    <th style={{ textAlign: 'left', padding: 8, color: '#5f6f66' }}>Ангилал</th>
                    <th style={{ textAlign: 'right', padding: 8, color: '#5f6f66' }}>Төлөвлөсөн</th>
                    <th style={{ textAlign: 'right', padding: 8, color: '#5f6f66' }}>Зарцуулсан</th>
                    <th style={{ textAlign: 'right', padding: 8, color: '#5f6f66' }}>Гүйцэтгэл</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.items.map((it: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 8, fontWeight: 500 }}>{CATEGORIES[it.category] || it.category}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>₮{it.planned.toLocaleString()}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>₮{it.actual.toLocaleString()}</td>
                      <td style={{ padding: 8, textAlign: 'right', color: it.execution_rate > 100 ? '#ef4444' : '#236844' }}>{it.execution_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'expenses' && (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {expenses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Зардал олдсонгүй</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8e5dc' }}>
                  <th style={thStyle}>Огноо</th>
                  <th style={thStyle}>Ангилал</th>
                  <th style={thStyle}>Тайлбар</th>
                  <th style={thStyle}>Нийлүүлэгч</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Дүн</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>{e.expense_date}</td>
                    <td style={tdStyle}>{CATEGORIES[e.category] || e.category}</td>
                    <td style={tdStyle}>{e.description}</td>
                    <td style={tdStyle}>{e.vendor || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>₮{e.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <Modal onClose={() => setShowBudgetModal(false)} title={editBudget ? 'Төсөв засах' : 'Шинэ төсөв'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input placeholder="Төсвийн нэр" value={budgetForm.name} onChange={e => setBudgetForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            <div style={{ display: 'flex', gap: 12 }}>
              <input type="number" placeholder="Он" value={budgetForm.year} onChange={e => setBudgetForm(f => ({ ...f, year: parseInt(e.target.value) || 2026 }))} style={{ ...inputStyle, flex: 1 }} />
              <select value={budgetForm.month} onChange={e => setBudgetForm(f => ({ ...f, month: e.target.value }))} style={{ ...inputStyle, flex: 1 }}>
                <option value="">Жилийн</option>
                {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}-р сар</option>)}
              </select>
            </div>
            <textarea placeholder="Тэмдэглэл" value={budgetForm.notes} onChange={e => setBudgetForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, minHeight: 60 }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#1c2b22' }}>Зардлын ангилал</span>
              <button onClick={addBudgetItem} style={smallBtn('#236844')}>+ Нэмэх</button>
            </div>

            {budgetForm.items.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={item.category} onChange={e => {
                  const items = [...budgetForm.items];
                  items[idx] = { ...items[idx], category: e.target.value };
                  setBudgetForm(f => ({ ...f, items }));
                }} style={{ ...inputStyle, flex: 1 }}>
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input placeholder="Тайлбар" value={item.description} onChange={e => {
                  const items = [...budgetForm.items];
                  items[idx] = { ...items[idx], description: e.target.value };
                  setBudgetForm(f => ({ ...f, items }));
                }} style={{ ...inputStyle, flex: 1 }} />
                <input type="number" placeholder="Дүн" value={item.planned_amount || ''} onChange={e => {
                  const items = [...budgetForm.items];
                  items[idx] = { ...items[idx], planned_amount: parseFloat(e.target.value) || 0 };
                  setBudgetForm(f => ({ ...f, items }));
                }} style={{ ...inputStyle, width: 120 }} />
                <button onClick={() => removeBudgetItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>
            ))}

            <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#1c2b22', marginTop: 4 }}>
              Нийт: ₮{budgetForm.items.reduce((s, i) => s + i.planned_amount, 0).toLocaleString()}
            </div>

            <button onClick={handleSaveBudget} style={{ ...btnStyle('#236844'), width: '100%', marginTop: 8 }}>
              {editBudget ? 'Хадгалах' : 'Үүсгэх'}
            </button>
          </div>
        </Modal>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <Modal onClose={() => setShowExpenseModal(false)} title="Зардал бүртгэх">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <select value={expenseForm.budget_id} onChange={e => setExpenseForm(f => ({ ...f, budget_id: e.target.value }))} style={inputStyle}>
              <option value="">Төсөвгүй</option>
              {budgets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select value={expenseForm.category} onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input placeholder="Тайлбар" value={expenseForm.description} onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} />
            <input type="number" placeholder="Дүн" value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} />
            <input type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm(f => ({ ...f, expense_date: e.target.value }))} style={inputStyle} />
            <input placeholder="Нийлүүлэгч" value={expenseForm.vendor} onChange={e => setExpenseForm(f => ({ ...f, vendor: e.target.value }))} style={inputStyle} />
            <button onClick={handleSaveExpense} style={{ ...btnStyle('#8b5cf6'), width: '100%', marginTop: 8 }}>Бүртгэх</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 560, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Inter'", fontWeight: 700, fontSize: 18, color: '#1c2b22', margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 13, color: '#5f6f66', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "'Inter'", fontSize: 22, fontWeight: 700, color: '#1c2b22' }}>{value}</div>
    </div>
  );
}

const btnStyle = (bg: string): React.CSSProperties => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px',
  fontWeight: 600, fontSize: 13, cursor: 'pointer',
});

const smallBtn = (color: string): React.CSSProperties => ({
  background: 'none', border: `1px solid ${color}`, color, borderRadius: 6,
  padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
});

const inputStyle: React.CSSProperties = {
  padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14,
  fontFamily: "'Inter'", outline: 'none',
};

const thStyle: React.CSSProperties = { textAlign: 'left', padding: '12px 16px', color: '#5f6f66', fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: '10px 16px', color: '#1c2b22' };
